import { format } from 'date-fns';
import { ConsiderationType } from 'further-types/exit';
import { ExitDetailsFormValues } from '../../ExitDetailsForm';
import { StagedShareholding } from '../../ExitDetailsForm/types';

type SharedShareConsiderationFields = {
  shareConsiderationCompanyId?: string;
  shareConsiderationShareClassId?: string;
  totalConsiderationShares?: number;
  shareConsiderationValuePerShare?: number;
  shareConsiderationValueTotal?: number;
  shareConsiderationEisEligible?: boolean;
  shareConsiderationAllocationMethod?: string;
  permitPartialShareAllocation?: boolean;
};

export type StageNewExitInput = {
  exitDate: string;
  firmId: string;
  companyIds: Array<string>;
  sharesToBeSold: number;
  sharePrice: number;
  taxableValueSharePrice: number;
  shareAllocationUploadIds: Array<string>;
  considerationType: string;
  permitPartialShareAllocation?: boolean;
  chargeAccruedFees?: boolean;
} & SharedShareConsiderationFields;

export type Mutation = {
  mutate: (
    data: StageNewExitInput,
    options: {
      onSuccess: (data: Array<StagedShareholding>) => void;
    },
  ) => void;
};

export const stageNewExit = (
  mutation: Mutation,
  exitDetails: ExitDetailsFormValues,
  setInitialShareholdings: (data: Array<StagedShareholding>) => void,
) => {
  const params = {
    exitDate: format(exitDetails.exitDate, 'dd/MM/yyyy'),
    companyIds: exitDetails.selectedShareClasses ?? [],
    firmId: exitDetails.firmId,
    sharePrice: Number(exitDetails.sharePrice),
    taxableValueSharePrice: Number(exitDetails.taxableValueSharePrice),
    sharesToBeSold: exitDetails.sharesToBeSold,
    shareAllocationUploadIds: exitDetails.selectedAllocations ?? [],
    considerationType: exitDetails.considerationType ?? ConsiderationType.Cash,
    permitPartialShareAllocation: exitDetails.permitPartialShareAllocation,
    chargeAccruedFees: exitDetails.chargeAccruedFees,
    ...(exitDetails.considerationType === ConsiderationType.Share && {
      shareConsiderationCompanyId: exitDetails.shareConsiderationCompanyId,
      shareConsiderationShareClassId:
        exitDetails.shareConsiderationShareClassId,
      totalConsiderationShares: exitDetails.totalConsiderationShares
        ? Number(exitDetails.totalConsiderationShares)
        : undefined,
      shareConsiderationValuePerShare:
        exitDetails.shareConsiderationValuePerShare
          ? Number(exitDetails.shareConsiderationValuePerShare)
          : undefined,
      shareConsiderationValueTotal: exitDetails.shareConsiderationValueTotal
        ? Number(exitDetails.shareConsiderationValueTotal)
        : undefined,
      shareConsiderationEisEligible: exitDetails.shareConsiderationEisEligible,
      shareConsiderationAllocationMethod:
        exitDetails.shareConsiderationAllocationMethod,
    }),
  };

  mutation.mutate(params, {
    onSuccess: (data: Array<StagedShareholding>) => {
      const formattedInitialShareholdingData: Array<StagedShareholding> =
        data?.map((sh) => ({
          ...sh,
          suggestedSharesAllocatedForSale:
            sh.sharesAllocatedForSale ?? undefined,
          // Do NOT overwrite investorTotalAccruedFees - it contains the investment-level max
          // that should be used as the limit for all shareholdings of the same investment
        })) ?? [];
      setInitialShareholdings(formattedInitialShareholdingData);
    },
  });
};
